import React from 'react';
import { FeatureFlags } from '@fabric-ui/components';
import { AccountUsage, Aging, Assets, Code, EasmSettings, Exceptions, Findings, Lock, Tenant, User, Workflows } from '@fabric-ui/icons';
import {
  Accounts,
  AgenticSoc,
  Agents,
  Alerts,
  Mention as Alias,
  AssetCoverage,
  AssetInventory,
  Authentications,
  CoverageAndGap,
  CustomDashboards,
  DataModel,
  DataRetention,
  DataUnification,
  Entities,
  FormattingRules,
  Script as GlobalScript,
  Incident as IncidentSideBar,
  Integration,
  Logs,
  LookUp as LookupTables,
  Measurements,
  Overview,
  Pivot,
  PolicyCompliance,
  PolicyViolations,
  Relations,
  Remediation,
  Reports,
  Risk,
  RiskScore,
  Roi as ROI,
  Rules,
  Scanner,
  Setting,
  Sources,
  Tickets,
  ViolationTickets,
} from '@fabric-ui/icons/screens';
import { APP_PATHS, PAGE_PATHS, Permission } from '../../types';
import { MenuItem } from '../../types/sideBar.types';

export const SIDEBAR_WIDTH_OPEN = 230;
export const SIDEBAR_WIDTH_COLLAPSED = 70;

export const DetectionsMenuItems: MenuItem[] = [
  {
    name: 'INVESTIGATE',
    subMenu: [
      { path: PAGE_PATHS.INCIDENTS, name: 'Incidents', icon: <IncidentSideBar /> },
      { path: PAGE_PATHS.ALERTS, name: 'Alerts', icon: <Alerts /> },
      {
        path: PAGE_PATHS.SETTINGS,
        name: 'Settings',
        icon: <Setting />,
        subMenu: [
          { path: PAGE_PATHS.RULE_SET, name: 'Grouping Rules' },
          { path: PAGE_PATHS.TICKETS_STATUSES, name: 'Incidents Statuses' },
          { path: PAGE_PATHS.UI_CONFIG, name: 'UI Config' },
        ],
      },
    ],
  },
  {
    name: 'DETECT',
    subMenu: [
      { path: PAGE_PATHS.RULES, name: 'Rules', icon: <Rules /> },
      {
        path: PAGE_PATHS.GLOBAL_SCRIPTS,
        name: 'Scripts Library',
        icon: <GlobalScript />,
      },
      {
        path: PAGE_PATHS.LOOKUP_TABLES,
        name: 'Lookup Tables',
        icon: <LookupTables />,
      },
    ],
  },
  {
    name: 'ANALYZE',
    subMenu: [
      { path: PAGE_PATHS.PIVOT, name: 'Pivot', icon: <Pivot /> },
      { path: PAGE_PATHS.REPORTS, name: 'My Reports', icon: <Reports /> },
      {
        path: PAGE_PATHS.CUSTOM_DASHBOARDS,
        name: 'My Dashboards',
        icon: <CustomDashboards />,
      },
    ],
  },
];

export const VulnerabilitiesMenuItems: MenuItem[] = [
  {
    name: 'REMEDIATE',
    subMenu: [
      { path: PAGE_PATHS.TICKETS, name: 'Tickets', icon: <Tickets /> },
      { path: PAGE_PATHS.ASSETS, name: 'Assets', icon: <Assets /> },
      { path: PAGE_PATHS.FINDINGS, name: 'Findings', icon: <Findings /> },
      { path: PAGE_PATHS.EXCEPTIONS, name: 'Exceptions', icon: <Exceptions /> },
      {
        path: PAGE_PATHS.SETTINGS,
        name: 'Settings',
        icon: <Setting />,
        subMenu: [
          { path: PAGE_PATHS.RULE_SET, name: 'Grouping Rules' },
          { path: PAGE_PATHS.SCORE, name: 'Score' },
          { path: PAGE_PATHS.SEVERITY_SLA, name: 'Severity & SLA' },
          { path: PAGE_PATHS.UI_CONFIG, name: 'UI Config' },
          { path: PAGE_PATHS.EXCEPTIONS, name: 'Exceptions' },
          { path: PAGE_PATHS.TICKET_LIFECYCLE, name: 'Ticket Lifecycle' },
          {
            path: PAGE_PATHS.EASM_SETTINGS,
            name: 'EASM Settings',
            ffForDisplaying: FeatureFlags.ShowAssetDiscoveryPage,
            externalLink: `$accountId/${APP_PATHS.PLATFORM}/${PAGE_PATHS.EASM_SETTINGS}`,
            permission: Permission.READ,
          },
        ],
      },
    ],
  },
  {
    name: 'ANALYZE',
    subMenu: [
      { path: PAGE_PATHS.OVERVIEW, name: 'Overview', icon: <Overview /> },
      {
        path: PAGE_PATHS.REMEDIATION,
        name: 'Remediation History',
        icon: <Remediation />,
      },
      { path: PAGE_PATHS.RISK, name: 'Risk', icon: <Risk /> },
      { path: PAGE_PATHS.PIVOT, name: 'Pivot', icon: <Pivot /> },
      {
        path: PAGE_PATHS.ASSET_COVERAGE,
        name: 'Asset Coverage',
        icon: <AssetCoverage />,
      },
      { path: PAGE_PATHS.ROI, name: 'ROI', icon: <ROI /> },
      { path: PAGE_PATHS.REPORTS, name: 'My Reports', icon: <Reports /> },
      {
        path: PAGE_PATHS.CUSTOM_DASHBOARDS,
        name: 'My Dashboards',
        icon: <CustomDashboards />,
      },
    ],
  },
];

export const IncidentsMenuItems: MenuItem[] = [
  {
    name: 'REMEDIATE',
    subMenu: [
      { path: PAGE_PATHS.INCIDENTS, name: 'Incidents', icon: <IncidentSideBar /> },
      {
        path: PAGE_PATHS.SETTINGS,
        name: 'Settings',
        icon: <Setting />,
        subMenu: [
          { path: PAGE_PATHS.RULE_SET, name: 'Grouping Rules' },
          { path: PAGE_PATHS.TICKETS_STATUSES, name: 'Incidents Statuses' },
          { path: PAGE_PATHS.UI_CONFIG, name: 'UI Config' },
        ],
      },
    ],
  },
  {
    name: 'ANALYZE',
    subMenu: [
      { path: PAGE_PATHS.PIVOT, name: 'Pivot', icon: <Pivot /> },
      { path: PAGE_PATHS.REPORTS, name: 'My Reports', icon: <Reports /> },
      {
        path: PAGE_PATHS.CUSTOM_DASHBOARDS,
        name: 'My Dashboards',
        icon: <CustomDashboards />,
      },
    ],
  },
];

export const ExploreMenuItems: MenuItem[] = [
  {
    name: 'EXPLORE',
    subMenu: [
      { path: PAGE_PATHS.ENTITIES, name: 'Entities', icon: <Entities /> },
      { path: PAGE_PATHS.LOGS, name: 'Logs', icon: <Logs /> },
      { path: PAGE_PATHS.REPORTS, name: 'Reports', icon: <Reports /> },
      { path: PAGE_PATHS.CUSTOM_DASHBOARDS, name: 'Dashboards', icon: <CustomDashboards /> },
    ],
  },
  {
    name: 'DETECT',
    subMenu: [],
  },
];

export const PlatformMenuItems: MenuItem[] = [
  {
    name: 'CONFIGURE',
    subMenu: [
      {
        path: PAGE_PATHS.PLATFORM_OVERVIEW,
        name: 'Overview',
        icon: <Overview />,
        ffForDisplaying: FeatureFlags.HealthDashboard,
        subMenu: [{ path: PAGE_PATHS.HEALTH_DASHBOARD, name: 'Health Dashboard', ffForDisplaying: FeatureFlags.HealthDashboard }],
      },
      { path: PAGE_PATHS.SOURCES, name: 'Sources', icon: <Sources /> },
      { path: PAGE_PATHS.TARGETS, name: 'Outegrations', icon: <Integration /> },
      {
        path: PAGE_PATHS.AUTHENTICATION_SOURCES,
        name: 'Authentications',
        icon: <Authentications />,
        ffForDisplaying: FeatureFlags.OneAuthUI,
      },
      {
        path: PAGE_PATHS.SCANNERS_ACTIVATION,
        name: 'Scanner Activation',
        icon: <Scanner />,
        ffForDisplaying: FeatureFlags.ScannersActivation,
      },
      { path: PAGE_PATHS.MODEL_MANAGEMENT, name: 'Data Model', icon: <DataModel /> },
      {
        path: PAGE_PATHS.DATA_UNIFICATION_ENTITIES,
        name: 'Data Unification',
        icon: <DataUnification />,
        subMenu: [
          { path: PAGE_PATHS.DATA_UNIFICATION_FIELDS, name: 'Fields' },
          { path: PAGE_PATHS.DATA_UNIFICATION_ENTITIES, name: 'Entities' },
        ],
      },
      {
        path: PAGE_PATHS.ASSET_AGING,
        name: 'Asset Aging',
        icon: <Aging />,
      },
      { path: PAGE_PATHS.WORKFLOWS, name: 'Workflows', icon: <Workflows /> },
      { path: PAGE_PATHS.RELATIONS, name: 'Relations', icon: <Relations /> },
      { path: PAGE_PATHS.ALIASES, name: 'Aliases', icon: <Alias /> },
      {
        path: PAGE_PATHS.DATA_PURGE,
        name: 'Data Purge',
        icon: <DataRetention />,
        ffForDisplaying: FeatureFlags.EnableDataRetentionDefaultRulesCreation,
      },
      {
        path: PAGE_PATHS.MEASUREMENTS,
        name: 'Measurements',
        icon: <Measurements />,
        permission: Permission.READ,
      },
      { path: PAGE_PATHS.FORMATTING_RULES, name: 'Formatting Rules', icon: <FormattingRules /> },
      {
        path: PAGE_PATHS.EASM_SETTINGS,
        name: 'EASM Settings',
        icon: <EasmSettings />,
        ffForDisplaying: FeatureFlags.ShowAssetDiscoveryPage,
        permission: Permission.READ,
      },
    ],
  },
  {
    name: 'UNIFIED APPS SETTINGS',
    subMenu: [{ path: PAGE_PATHS.RISK_SCORE, name: 'Risk Score', icon: <RiskScore />, ffForDisplaying: FeatureFlags.AssetRiskScore }],
  },
];

export const SettingsMenuItems: MenuItem[] = [
  {
    name: 'PROFILE',
    subMenu: [{ path: PAGE_PATHS.EDIT_MY_USER, name: 'Settings', icon: <Setting /> }],
  },
  {
    name: 'ACCOUNT SETTINGS',
    subMenu: [
      { path: PAGE_PATHS.EDIT_MY_ACCOUNT_MANAGEMENT, name: 'Settings', icon: <Setting /> },
      { path: PAGE_PATHS.USER_MANAGEMENT, name: 'User Management', icon: <User /> },
      {
        name: 'Permissions',
        path: PAGE_PATHS.PERMISSIONS,
        icon: <Lock />,
        subMenu: [
          { path: PAGE_PATHS.ROLES, name: 'Roles' },
          { path: PAGE_PATHS.CONTENT_PERMISSIONS, name: 'Content' },
        ],
      },
      {
        path: PAGE_PATHS.ACCOUNT_USAGE,
        name: 'Asset Plan Utilization',
        icon: <AccountUsage />,
        isBackOffice: true,
        ffForDisplaying: FeatureFlags.accountUsagePage,
      },
    ],
  },
  {
    name: 'MANAGE ACCOUNTS',
    subMenu: [
      { path: PAGE_PATHS.ALL_USERS, name: 'All Users', icon: <User />, isBackOffice: true },
      { path: PAGE_PATHS.ALL_ACCOUNTS, name: 'All Accounts', icon: <Accounts />, permission: Permission.CREATE },
    ],
  },
  {
    name: 'INTERNAL ADMIN TOOLS',
    subMenu: [
      { path: PAGE_PATHS.BACKOFFICE_ACTIONS, name: 'Backoffice Actions', icon: <Setting />, isBackOffice: true },
      { path: PAGE_PATHS.QUERY, name: 'Query Runner', icon: <Code />, permission: Permission.UPDATE },
      { path: PAGE_PATHS.ICONS, name: 'Logos Gallery', icon: <Sources /> },
      {
        path: PAGE_PATHS.RISK360_BACKOFFICE_ACTIONS,
        name: 'Risk360 Backoffice',
        icon: <Setting />,
        permission: Permission.UPDATE,
      },
      { path: PAGE_PATHS.GLOBAL_FACTOR_RULES, name: 'Global Factor Rules', icon: <Rules /> },
    ],
  },
];

export const Risk360MenuItems: MenuItem[] = [
  {
    name: 'RISK MANAGEMENT',
    subMenu: [
      {
        path: PAGE_PATHS.FACTOR,
        name: 'Factor',
        icon: <Rules />,
      },
      {
        path: PAGE_PATHS.FACTOR_RULES,
        name: 'Factor Rules',
        icon: <Rules />,
      },
    ],
  },
  {
    name: 'ANALYZE',
    subMenu: [
      {
        path: PAGE_PATHS.CUSTOM_DASHBOARDS,
        name: 'My Dashboards',
        icon: <CustomDashboards />,
      },
    ],
  },
];

export const AssetsMenuItems: MenuItem[] = [
  {
    name: 'REMEDIATE',
    subMenu: [
      { path: PAGE_PATHS.ASSETS, name: 'Assets', icon: <Assets /> },
      { path: PAGE_PATHS.VIOLATION_TICKETS, name: 'Violation Tickets', icon: <ViolationTickets /> },
      { path: PAGE_PATHS.POLICY_VIOLATIONS, name: 'Policy Violations', icon: <PolicyViolations /> },
      { path: PAGE_PATHS.USERS, name: 'Users', icon: <User />, ffForDisplaying: FeatureFlags.ShowingIdentityInAssetsApp },
      { path: PAGE_PATHS.POLICIES, name: 'Policies', icon: <Rules /> },
      {
        path: PAGE_PATHS.SETTINGS,
        name: 'Settings',
        icon: <Setting />,
        subMenu: [
          { path: PAGE_PATHS.RULE_SET, name: 'Grouping Rules' },
          { path: PAGE_PATHS.UI_CONFIG, name: 'UI Config' },
          {
            path: PAGE_PATHS.TICKET_LIFECYCLE,
            name: 'Violation Ticket Lifecycle',
            fixedTabHeight: 52,
          },
          {
            path: PAGE_PATHS.EASM_SETTINGS,
            name: 'EASM Settings',
            ffForDisplaying: FeatureFlags.ShowAssetDiscoveryPage,
            externalLink: `$accountId/${APP_PATHS.PLATFORM}/${PAGE_PATHS.EASM_SETTINGS}`,
            permission: Permission.READ,
          },
        ],
      },
    ],
  },
  {
    name: 'ANALYZE',
    subMenu: [
      {
        path: PAGE_PATHS.POLICY_COMPLIANCE,
        name: 'Policy Compliance',
        icon: <PolicyCompliance />,
      },
      {
        path: PAGE_PATHS.ASSET_INVENTORY,
        name: 'Asset Inventory',
        icon: <AssetInventory />,
      },
      {
        path: PAGE_PATHS.TOOL_COVERAGE_AND_GAP,
        name: 'Coverage And Gap',
        icon: <CoverageAndGap />,
      },
      { path: PAGE_PATHS.REPORTS, name: 'My Reports', icon: <Reports /> },
      {
        path: PAGE_PATHS.CUSTOM_DASHBOARDS,
        name: 'My Dashboards',
        icon: <CustomDashboards />,
      },
    ],
  },
];

export const ThreatsMenuItems: MenuItem[] = [
  {
    name: 'INVESTIGATE',
    subMenu: [
      {
        path: PAGE_PATHS.AGENTIC_SOC,
        name: 'Agentic SOC',
        icon: <AgenticSoc />,
        ffForDisplaying: FeatureFlags.ShowIncidentFlow,
        landingPage: true,
      },
      { path: PAGE_PATHS.INCIDENTS, name: 'Incidents', icon: <IncidentSideBar /> },
      { path: PAGE_PATHS.ALERTS, name: 'Alerts', icon: <Alerts /> },
      { path: PAGE_PATHS.ASSETS, name: 'Assets', icon: <Assets /> },
      { path: PAGE_PATHS.USERS, name: 'Users', icon: <User /> },
      {
        path: PAGE_PATHS.SETTINGS,
        name: 'Settings',
        icon: <Setting />,
        subMenu: [
          { path: PAGE_PATHS.ALERT_SCORE, name: 'Scoring' },
          { path: PAGE_PATHS.INCIDENT_RULES, name: 'Incident Rules' },
          { path: PAGE_PATHS.TICKETS_STATUSES, name: 'Statuses' },
          { path: PAGE_PATHS.SEVERITY, name: 'Severity' },
          { path: PAGE_PATHS.UI_CONFIG, name: 'UI Config' },
          { path: PAGE_PATHS.INCIDENT_LIFECYCLE, name: 'Incident Lifecycle', ffForDisplaying: FeatureFlags.IncidentLifecycle },
        ],
      },
    ],
  },
  {
    name: 'ANALYZE',
    subMenu: [
      {
        path: PAGE_PATHS.THREAT_DASHBOARD,
        name: 'Dashboard',
        icon: <Pivot />,
        ffForDisplaying: FeatureFlags.ShowThreatDashboard,
        landingPage: true,
      },
      { path: PAGE_PATHS.AGENTS, name: 'Agents', icon: <Agents />, ffForDisplaying: FeatureFlags.AgentDashboard },
      { path: PAGE_PATHS.REPORTS, name: 'My Reports', icon: <Reports /> },
      { path: PAGE_PATHS.CUSTOM_DASHBOARDS, name: 'My Dashboards', icon: <CustomDashboards /> },
    ],
  },
];

export const IdentitiesMenuItems: MenuItem[] = [
  {
    name: 'REMEDIATE',
    subMenu: [
      { path: PAGE_PATHS.USERS, name: 'Users', icon: <User /> },
      { path: PAGE_PATHS.TENANTS, name: 'Tenants', icon: <Tenant /> },
      { path: PAGE_PATHS.IDENTITY_FINDINGS, name: 'Identity Findings', icon: <Findings /> },
      { path: PAGE_PATHS.ALERTS, name: 'Alerts', icon: <Alerts /> },
      {
        path: PAGE_PATHS.SETTINGS,
        name: 'Settings',
        icon: <Setting />,
        subMenu: [
          { path: PAGE_PATHS.UI_CONFIG, name: 'UI Config' },
          { path: PAGE_PATHS.ITDR_CONFIGURATION, name: 'ITDR Config' },
        ],
      },
    ],
  },
  {
    name: 'ANALYZE',
    subMenu: [
      { path: PAGE_PATHS.IDNP_REMEDIATION_DASHBOARD, name: 'Remediation', icon: <Remediation /> },
      { path: PAGE_PATHS.IDNP_OVERVIEW_DASHBOARD, name: 'Overview', icon: <Overview />, landingPage: true },
      { path: PAGE_PATHS.REPORTS, name: 'My Reports', icon: <Reports /> },
      {
        path: PAGE_PATHS.CUSTOM_DASHBOARDS,
        name: 'My Dashboards',
        icon: <CustomDashboards />,
      },
    ],
  },
];

export const appsSideBar: Record<string, MenuItem[]> = {
  [APP_PATHS.DETECTIONS]: DetectionsMenuItems,
  [APP_PATHS.VULNERABILITIES]: VulnerabilitiesMenuItems,
  [APP_PATHS.INCIDENTS]: IncidentsMenuItems,
  [APP_PATHS.PLATFORM]: PlatformMenuItems,
  [APP_PATHS.EXPLORE]: ExploreMenuItems,
  [APP_PATHS.SETTINGS]: SettingsMenuItems,
  [APP_PATHS.RISK360]: Risk360MenuItems,
  [APP_PATHS.ASSETS]: AssetsMenuItems,
  [APP_PATHS.THREATS]: ThreatsMenuItems,
  [APP_PATHS.IDENTITIES]: IdentitiesMenuItems,
};
